/* ═══════════════════════════════════════════════════════════════
   Time & Expense rebuild (Tranche 4) — one expense row's attachments.

   Fully lazy: nothing is fetched until the user expands the row, and
   even then only metadata (id/fileName/mimeType/createdAt) comes back
   — never the base64 `data` column. The base64 payload for a single
   file is only ever fetched the moment the user clicks Download for
   THAT file (ExpenseService.getAttachmentData), never in bulk, never
   on page open. This is what kills the old page's behavior of loading
   every receipt's bytes for every expense the instant the tab opened.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Download, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';

import { ExpenseService } from '../../services/expenseService';
import type { ExpenseAttachment } from '../../../shared/types';

export type AttachmentMeta = Omit<ExpenseAttachment, 'data'>;

export interface AttachmentsCellProps {
  expenseId: string;
  isEditable: boolean;
  toast: (msg: string, isError?: boolean) => void;
}

export function AttachmentsCell({ expenseId, isEditable, toast }: AttachmentsCellProps) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AttachmentMeta[] | null>(null); // null = never fetched
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  async function loadIfNeeded() {
    if (items !== null) return;
    setLoading(true);
    try {
      const list = await ExpenseService.listAttachmentsMeta(expenseId);
      setItems(list);
    } catch (err: any) {
      toast('Failed to load attachments: ' + err.message, true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (next) await loadIfNeeded();
  }

  async function handleAttach() {
    try {
      const file = await ExpenseService.openFileDialog();
      if (!file) return;
      const saved = await ExpenseService.saveAttachment({
        id: '',
        expenseId,
        fileName: file.fileName,
        mimeType: file.mimeType,
        data: file.data,
        createdAt: '',
      });
      setItems((prev) => [
        ...(prev || []),
        {
          id: saved.id,
          expenseId: saved.expenseId,
          fileName: saved.fileName,
          mimeType: saved.mimeType,
          createdAt: saved.createdAt,
        },
      ]);
      setExpanded(true);
      toast('Attachment added.');
    } catch (err: any) {
      toast('Attach failed: ' + err.message, true);
    }
  }

  async function handleDownload(att: AttachmentMeta) {
    setDownloadingId(att.id);
    try {
      const full = await ExpenseService.getAttachmentData(att.id);
      if (!full) throw new Error('Attachment not found');
      const a = document.createElement('a');
      a.href = `data:${full.mimeType};base64,${full.data}`;
      a.download = full.fileName;
      a.click();
    } catch (err: any) {
      toast('Download failed: ' + err.message, true);
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleDelete(attId: string) {
    try {
      await ExpenseService.deleteAttachment(attId);
      setItems((prev) => (prev || []).filter((a) => a.id !== attId));
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: rem(2), justifyContent: 'center' }}>
        {items && items.length > 0 && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--ac)', fontWeight: 700 }}>
            {items.length}
          </span>
        )}
        {isEditable && (
          <Button
            variant="ghost"
            size="icon"
            title="Attach file"
            aria-label="Attach file"
            onClick={handleAttach}
          >
            <Icon icon={Plus} em={1.0769} />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          title={expanded ? 'Hide attachments' : 'View attachments'}
          aria-label={expanded ? 'Hide attachments' : 'View attachments'}
          onClick={toggle}
        >
          {expanded ? (
            <Icon icon={ChevronUp} em={1.0769} />
          ) : (
            <Icon icon={ChevronDown} em={1.0769} />
          )}
        </Button>
      </div>
      {expanded && (
        <div
          style={{
            marginTop: 'var(--space-1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            alignItems: 'flex-start',
          }}
        >
          {loading && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>Loading…</span>
          )}
          {!loading && items && items.length === 0 && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>No files</span>
          )}
          {!loading &&
            items?.map((att) => (
              <div
                key={att.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  fontSize: 'var(--font-size-xs)',
                  background: 'var(--sa)',
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-sm)',
                  padding: `${rem(2)} var(--space-1)`,
                }}
              >
                <span
                  style={{
                    color: 'var(--tx)',
                    maxWidth: rem(120),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={att.fileName}
                >
                  {att.fileName}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Download"
                  aria-label={`Download ${att.fileName}`}
                  disabled={downloadingId === att.id}
                  onClick={() => handleDownload(att)}
                >
                  <Icon icon={Download} em={1.2} />
                </Button>
                {isEditable && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Remove attachment"
                    aria-label={`Remove ${att.fileName}`}
                    onClick={() => handleDelete(att.id)}
                  >
                    <Icon icon={Trash2} em={1.2} />
                  </Button>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
